import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download, Receipt, CheckCircle, ArrowUUpLeft, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * InvoicesDialog — lists invoices attached to a PO, and (for lab/ops managers)
 * allows uploading additional invoice PDFs. Finance can toggle paid/pending.
 */
const InvoicesDialog = ({ po, open, onClose, onChanged }) => {
  const { user, getAuthHeader } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const canUpload = user?.role === 'lab_manager' || user?.role === 'operations_manager';
  const isFinance = user?.role === 'finance';
  const isAdmin = user?.role === 'admin';

  const load = async () => {
    if (!po) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/purchase-orders/${po.id}/invoices`, { headers: getAuthHeader() });
      setInvoices(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && po) {
      load();
      setShowUpload(false);
      setInvoiceNumber('');
      setAmount('');
      setNotes('');
      setFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, po?.id]);

  const handleUpload = async () => {
    if (!invoiceNumber.trim() || !amount || !file) {
      toast.error('Invoice number, amount and PDF file are required');
      return;
    }
    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      toast.error('Amount must be a positive number');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('invoice_number', invoiceNumber.trim());
      form.append('amount', String(numericAmount));
      if (notes.trim()) form.append('notes', notes.trim());
      form.append('file', file);
      await axios.post(`${API}/purchase-orders/${po.id}/invoices`, form, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Invoice ${invoiceNumber.trim()} uploaded — Finance notified`);
      setShowUpload(false);
      setInvoiceNumber('');
      setAmount('');
      setNotes('');
      setFile(null);
      load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload invoice');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (inv) => {
    try {
      const response = await axios.get(`${API}/invoices/${inv.id}/pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', inv.invoice_file_name || `Invoice_${inv.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download invoice');
    }
  };

  const handleToggle = async (inv) => {
    const markPaid = inv.status !== 'paid';
    try {
      await axios.post(
        `${API}/invoices/${inv.id}/mark-paid`,
        { paid: markPaid },
        { headers: getAuthHeader() }
      );
      toast.success(markPaid
        ? `Invoice ${inv.invoice_number} marked as PAID`
        : `Invoice ${inv.invoice_number} reverted to PENDING`);
      load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async (inv) => {
    if (!window.confirm(
      `Delete invoice ${inv.invoice_number}?\n\nThis removes the PDF and creates an audit-log entry. You can then upload the correct one.`
    )) return;
    try {
      await axios.delete(`${API}/invoices/${inv.id}`, { headers: getAuthHeader() });
      toast.success(`Invoice ${inv.invoice_number} deleted`);
      load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  if (!po) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="sm:max-w-3xl" data-testid="invoices-dialog">
        <DialogHeader>
          <DialogTitle>Invoices — PO {po.po_number}</DialogTitle>
          <DialogDescription>
            Upload vendor invoices against this released PO so Finance can process payment. Multiple invoices per PO are supported.
          </DialogDescription>
        </DialogHeader>

        <div className="text-sm text-text-secondary flex items-center justify-between">
          <span>
            Vendor: <strong>{po.vendor_name}</strong> · PO total: <strong>₹{Number(po.total_amount || 0).toFixed(2)}</strong>
          </span>
          {canUpload && !showUpload && (
            <Button
              size="sm"
              className="bg-accent-brand hover:bg-accent-brand-hover text-white"
              onClick={() => setShowUpload(true)}
              data-testid="show-upload-invoice-btn"
            >
              <Receipt size={16} className="mr-1" weight="duotone" />
              Upload Invoice
            </Button>
          )}
        </div>

        {canUpload && showUpload && (
          <div className="mt-2 border border-border-default rounded p-4 space-y-3 bg-surface" data-testid="upload-invoice-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="inv_number">Invoice Number <span className="text-red-500">*</span></Label>
                <Input
                  id="inv_number"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="e.g., INV-2026-0234"
                  data-testid="invoice-number-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="inv_amount">Amount (INR) <span className="text-red-500">*</span></Label>
                <Input
                  id="inv_amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="e.g., 12500.00"
                  data-testid="invoice-amount-input"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="inv_file">Invoice PDF <span className="text-red-500">*</span></Label>
              <input
                id="inv_file"
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="invoice-file-input"
                className="block w-full text-sm text-text-secondary file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-accent-brand-subtle file:text-accent-brand hover:file:bg-accent-brand-subtle"
              />
              {file && <p className="text-xs text-text-tertiary">Selected: <span className="font-mono">{file.name}</span> ({(file.size / 1024).toFixed(0)} KB)</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="inv_notes">Notes (optional)</Label>
              <textarea
                id="inv_notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., partial invoice covering 3 of 5 items…"
                className="w-full min-h-[50px] rounded border border-border-default px-3 py-2 text-sm"
                data-testid="invoice-notes-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowUpload(false)} disabled={uploading} data-testid="cancel-upload-invoice-btn">Cancel</Button>
              <Button
                className="bg-accent-brand hover:bg-accent-brand-hover text-white"
                disabled={uploading || !invoiceNumber.trim() || !amount || !file}
                onClick={handleUpload}
                data-testid="submit-invoice-btn"
              >
                {uploading ? 'Uploading…' : 'Upload Invoice'}
              </Button>
            </div>
          </div>
        )}

        <div className="max-h-[420px] overflow-y-auto mt-4">
          {loading ? (
            <p className="text-center py-6 text-text-tertiary text-sm">Loading…</p>
          ) : invoices.length === 0 ? (
            <p className="text-center py-8 text-text-tertiary text-sm" data-testid="invoices-empty">
              No invoices uploaded yet for this PO.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-surface border-b border-border-default sticky top-0">
                <tr>
                  <th className="p-2 text-left">Invoice #</th>
                  <th className="p-2 text-right">Amount</th>
                  <th className="p-2 text-left">Uploaded by</th>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-border-default" data-testid={`invoice-row-${inv.id}`}>
                    <td className="p-2 font-mono font-semibold text-text-primary">{inv.invoice_number}</td>
                    <td className="p-2 text-right font-semibold">₹{Number(inv.amount || 0).toFixed(2)}</td>
                    <td className="p-2 text-text-secondary">
                      {inv.uploaded_by_name}
                      <div className="text-xs text-text-tertiary">
                        {(inv.uploaded_by_role || '').replace(/_/g, ' ')}
                      </div>
                    </td>
                    <td className="p-2 text-text-secondary">{new Date(inv.uploaded_at).toLocaleDateString()}</td>
                    <td className="p-2">
                      <span className={`status-badge ${inv.status === 'paid' ? 'status-approved' : 'status-pending'}`} data-testid={`invoice-status-${inv.id}`}>
                        {inv.status}
                      </span>
                      {inv.status === 'paid' && inv.paid_by_name && (
                        <div className="text-xs text-text-tertiary mt-1">by {inv.paid_by_name}</div>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(inv)}
                          data-testid={`download-invoice-${inv.id}`}
                        >
                          <Download size={14} className="mr-1" weight="duotone" />
                          PDF
                        </Button>
                        {isFinance && (
                          inv.status === 'paid' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggle(inv)}
                              data-testid={`unmark-paid-${inv.id}`}
                              title="Revert to pending"
                            >
                              <ArrowUUpLeft size={14} className="mr-1" />
                              Undo
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              className="bg-accent-brand hover:bg-accent-brand-hover text-white"
                              onClick={() => handleToggle(inv)}
                              data-testid={`mark-paid-${inv.id}`}
                            >
                              <CheckCircle size={14} className="mr-1" weight="fill" />
                              Mark Paid
                            </Button>
                          )
                        )}
                        {inv.status !== 'paid' && (isAdmin || inv.uploaded_by === user?.id) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(inv)}
                            data-testid={`delete-invoice-${inv.id}`}
                            title="Delete this invoice so you can upload a corrected PDF"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Trash size={14} className="mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoicesDialog;
